import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { client } from '..';
import { ADD_FINANCE_RECORD, GET_ALL_FINANCE_RECORDS, SOFT_DELETE_FINANCE_RECORD ,EDIT_FINANCE_RECORD,UPDATE_ATTACHMENTS_FINANCE,REMOVE_ATTACHMENT_FINANCE} from '../Queries/finance';
import dayjs from 'dayjs';
import { setLoader } from './loaderSlice';

// 1. Async thunk for adding finance entry
export const addFinanceEntry = createAsyncThunk(
  'finance/addFinanceEntry',
  async (entry, { rejectWithValue, dispatch }) => {
    try {
      const spentByValue = Array.isArray(entry.spentBy)
        ? entry.spentBy.join(', ')
        : entry.spentBy;
      dispatch(setLoader(true));

      const { data } = await client.mutate({
        mutation: ADD_FINANCE_RECORD,
        variables: {
          date: entry.date,
          category: entry.category,
          subCategory: entry.subCategory,
          purchaseDetails: entry.purchaseDetails,
          invoice: entry.invoice,
          quantity: entry.quantity?.toString() || null,
          amount: entry.amount?.toString() || null,
          spentBy: spentByValue,
          transactionMode: entry.transactionMode,
          remarks: entry.remarks,
          attachemnts: entry.attachemnts,
          userName: entry.userName,
        },
      });

      dispatch(addRecordToAllRecords(data.addFinanceRecord));
      dispatch(setLoader(false));

      return data.addFinanceRecord;
    } catch (error) {
      dispatch(setLoader(false));

 
      return rejectWithValue(error.message || 'Failed to add finance entry');
    }
  }
);

export const GetAllRecords = createAsyncThunk(
  'records/getAll',
  async (form, { dispatch, rejectWithValue }) => {
    
    dispatch(setLoader(true));

    try {
      const response = await client.query({
        fetchPolicy: "network-only",
        query: GET_ALL_FINANCE_RECORDS,
        variables: {
          page: form.page ? form.page : 1,
          pageSize: form.pageSize ? form.pageSize : 50,
          from: form.from ? dayjs(form.from)?.startOf('day').toDate().toISOString() : null,
          to: form.to ? dayjs(form.to)?.endOf('day').toDate().toISOString() : null,
          year: form.year,
          month: form.month,
          spentBy: form.spentBy,
          category: form.category,
          subCategory: form.subCategory,
          purchaseDetails: form.purchaseDetails,
          invoice: form.invoice,
          quantity: form.quantity,
          amount: form.amount,
          transactionMode: form.transactionMode,
        },
      });

      dispatch(setAllRecords(response.data.getAllFinanceRecords));
      dispatch(setLoader(false));
      return response.data;
    } catch (err) {
      dispatch(setLoader(false));
      return rejectWithValue(err.message || "Failed to fetch records");
    }
  }
);

export const editFinanceThunk = createAsyncThunk(
  'finance/editFinanceRecord',
  async (formData, { rejectWithValue, dispatch }) => {
    try {
      dispatch(setLoader(true));

      const spentByValue = Array.isArray(formData.spentBy)
        ? formData.spentBy.join(', ')
        : formData.spentBy;

      const response = await client.mutate({
        mutation: EDIT_FINANCE_RECORD,
        variables: {
          id: formData.id,
          date: formData.date,
          category: formData.category,
          subCategory: formData.subCategory,
          purchaseDetails: formData.purchaseDetails,
          invoice: formData.invoice,
          quantity: formData.quantity?.toString() || null,
          amount: formData.amount?.toString() || null,
          spentBy: spentByValue,
          transactionMode: formData.transactionMode,
          remarks: formData.remarks,
          userName: formData.userName,
          task: formData.task || "Edit",
          attachemnts: formData.attachemnts || [],
        },
      });

      dispatch(setLoader(false));
      return response.data.editFinanceRecord;
    } catch (error) {
      dispatch(setLoader(false));
      return rejectWithValue(error.message || "Failed to edit record");
    }
  }
);


export const softDeleteThunk = createAsyncThunk(
  'finance/softDelete',
  async ({ id, userName, editedAt }, { rejectWithValue }) => {
    try {
      const response = await client.mutate({
        mutation: SOFT_DELETE_FINANCE_RECORD,
        variables: { id, userName, editedAt },
        fetchPolicy: "network-only",
      });

      if (response.data.deleteFinanceRecord) {
        return { success: true, deletedId: id }; // ✅ returning ID here
      } else {
        throw new Error("Failed to delete record");
      }
    } catch (error) {
      return rejectWithValue(error.message || "Failed to delete record");
    }
  }
);

export const updateFinanceAttachmentThunk = createAsyncThunk(
  'finance/updateAttachment',
  async ({ id, input }, { rejectWithValue }) => {
    try {
      const { data } = await client.mutate({
        mutation: UPDATE_ATTACHMENTS_FINANCE,
        variables: { id, input },
      });

      if (data.modifyAttachmentDetailsFinance?.status) {
        return data.modifyAttachmentDetailsFinance;
      } else {
        throw new Error('Failed to update attachment');
      }
    } catch (error) {
      return rejectWithValue(error.message || 'Failed to update attachment');
    }
  }
);

export const removeFinanceAttachmentThunk = createAsyncThunk(
  'finance/removeAttachment',
  async ({ id, userId, input }, { rejectWithValue }) => {
    try {
      const { data } = await client.mutate({
        mutation: REMOVE_ATTACHMENT_FINANCE,
        variables: { id, userId, input },
      });

      if (data.removeAttachmentFince?.status) {
        return data.removeAttachmentFince;
      } else {
        throw new Error('Failed to delete attachment');
      }
    } catch (error) {
      return rejectWithValue(error.message || 'Failed to delete attachment');
    }
  }
);


// 2. Initial state
const initialState = {

  financeRecords: [], // You can fill this on fetch if needed
  WorkHistory: [],
  AllRecords: {
    records: [],    // Array of records
    totalCount: 0,  // Total number of records
  },
};

// 3. Create finance slice
const financeSlice = createSlice({
  name: 'finance',
  initialState,
  reducers: {
    setFinanceRecords: (state, action) => {
      state.financeRecords = action.payload;
    },
    setFinanceHistory: (state, action) => {
      state.WorkHistory = action.payload;
    },
    setAllRecords: (state, action) => {
      state.AllRecords = action.payload;
    },
    addRecordToAllRecords: (state, action) => {
      if (state.AllRecords && Array.isArray(state.AllRecords.records)) {
        state.AllRecords.records = [...state.AllRecords.records, action.payload];
        state.AllRecords.totalCount += 1; // Optionally, increment totalCount if needed
      } else {

        state.AllRecords = {
          records: [action.payload],
          totalCount: 1, // Adjust accordingly
        };
      }
    },
    updateRecordInAllRecords: (state, action) => {
      const index = state.AllRecords.records.findIndex(
        (record) => record.id === action.payload.id
      );
      if (index !== -1) {
        state.AllRecords.records[index] = action.payload;  // Update the record
      }
    },
    resetFinanceStatus: (state) => {
      // You can reset any fields you need here
      // For example:
      state.status = null;
    },
  },
  extraReducers: (builder) => {
    builder.addCase(addFinanceEntry.fulfilled, (state, action) => {
      state.financeRecords.push(action.payload);
    })
      .addCase(editFinanceThunk.fulfilled, (state, action) => {
        const updated = action.payload;
        const index = state.AllRecords.records.findIndex((r) => r.id === updated.id);
        if (index !== -1) {
          state.AllRecords.records[index] = updated;
        }
      })
      .addCase(softDeleteThunk.fulfilled, (state, action) => {
        const deletedId = action.payload?.deletedId;
        if (deletedId) {
          state.AllRecords.records = state.AllRecords.records.filter(
            (item) => item.id !== deletedId
          );
          state.AllRecords.totalCount -= 1;
        }
      });
  },
});


export const { resetFinanceStatus, setAllRecords, setFinanceRecords, setFinanceHistory, addRecordToAllRecords, updateRecordInAllRecords } = financeSlice.actions;
export default financeSlice.reducer;
